import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeftIcon, LockClosedIcon, XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, formatApiError } from '../services/api';

const EditProfile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: user?.name || '' });
  
  // Password Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwData, setPwData] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleNameChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handlePwChange = (e) => setPwData({ ...pwData, [e.target.name]: e.target.value });

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return toast.error('Nama harus diisi');
    
    setLoading(true);
    try {
      const response = await authAPI.update(formData);
      toast.success(response.data.message || 'Profil berhasil diupdate');
      
      // Update AuthContext globally so the UI reacts immediately
      if (response.data.user) {
        updateUser(response.data.user);
      }
      
      navigate('/profile');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (user?.has_password && !pwData.old_password) return toast.error('Password lama harus diisi');
    if (pwData.new_password.length < 6) return toast.error('Password baru minimal 6 karakter');
    if (pwData.new_password !== pwData.confirm_password) return toast.error('Konfirmasi password tidak cocok');

    setPwLoading(true);
    try {
      const response = await authAPI.updatePassword({
        old_password: pwData.old_password,
        new_password: pwData.new_password
      });
      toast.success(response.data.message || 'Password berhasil diubah');
      setIsModalOpen(false);
      setPwData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* App Bar */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="flex items-center px-4 h-16">
          <button onClick={() => navigate('/profile')} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-slate-700" />
          </button>
          <h1 className="text-lg font-semibold text-slate-900 ml-2">Edit Profil</h1>
        </div>
      </div>

      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          
          {/* Status Akun Badge */}
          <div className="mb-6 flex flex-col items-center justify-center p-4 bg-surface rounded-2xl border border-slate-100">
            <img src={`https://api.dicebear.com/9.x/micah/svg?seed=${user?.name || 'User'}`} alt="Avatar" className="w-20 h-20 rounded-full border-2 border-white shadow-soft mb-3" />
            <p className="text-slate-500 text-sm mb-2">{user?.email}</p>
            {user?.is_google_linked ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                Terhubung dengan Google
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800">
                Email Standar
              </span>
            )}
          </div>

          <form className="space-y-6" onSubmit={handleNameSubmit}>
            <div className="bg-surface p-6 rounded-[24px] space-y-4 shadow-sm border border-slate-100">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Nama Lengkap</label>
                <input type="text" name="name" value={formData.name} onChange={handleNameChange}
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-slate-900" placeholder="Masukkan nama" />
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-primary text-white py-4 rounded-full font-medium hover:bg-primary-hover active:scale-95 transition-all shadow-float disabled:opacity-50">
              {loading ? 'Menyimpan...' : 'Simpan Nama'}
            </button>
          </form>

          <div className="mt-8 border-t border-slate-100 pt-6">
            <button type="button" onClick={() => setIsModalOpen(true)} className="w-full flex items-center justify-center gap-2 bg-white text-slate-700 py-4 rounded-full font-medium border border-slate-300 hover:bg-slate-50 transition-all shadow-sm active:scale-95">
              <LockClosedIcon className="w-5 h-5" /> Ubah Password
            </button>
          </div>
        </motion.div>
      </div>

      {/* Password Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsModalOpen(false)} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="relative w-full max-w-sm bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-bold text-slate-900">Ubah Password</h3>
                  <button onClick={() => setIsModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  {user?.has_password && (
                    <div>
                      <label className="text-xs font-semibold text-slate-500 mb-1 block">Password Lama</label>
                      <div className="relative">
                        <input type={showOld ? "text" : "password"} name="old_password" value={pwData.old_password} onChange={handlePwChange} className="w-full pl-4 pr-12 py-3 bg-surface border border-slate-200 rounded-xl outline-none focus:border-primary text-sm transition-all" required />
                        <button type="button" onClick={() => setShowOld(!showOld)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                          {showOld ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Password Baru</label>
                    <div className="relative">
                      <input type={showNew ? "text" : "password"} name="new_password" value={pwData.new_password} onChange={handlePwChange} className="w-full pl-4 pr-12 py-3 bg-surface border border-slate-200 rounded-xl outline-none focus:border-primary text-sm transition-all" required />
                      <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                        {showNew ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Konfirmasi Password Baru</label>
                    <div className="relative">
                      <input type={showConfirm ? "text" : "password"} name="confirm_password" value={pwData.confirm_password} onChange={handlePwChange} className="w-full pl-4 pr-12 py-3 bg-surface border border-slate-200 rounded-xl outline-none focus:border-primary text-sm transition-all" required />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                        {showConfirm ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" disabled={pwLoading} className="w-full bg-primary text-white py-3 mt-2 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all disabled:opacity-50">
                    {pwLoading ? 'Memproses...' : 'Simpan Password'}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EditProfile;
